/**
 * Central state management hook for the chatbot feature.
 * Handles messaging, streaming, trade confirmations,
 * and retry logic with exponential backoff.
 */
import { useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './useAuth';
import { sendChatMessage, AuthError, ApiError } from '@/services/chatApi';
import type { AgentType } from '@/services/chatApi';
import type {
  ChatMessageData,
  ChatAttachment,
  PendingTrade,
  Activity,
} from '@/types/chat';

// --- Constants ---

const TRADE_CONFIRMATION_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;
const RESPONSE_TIMEOUT_MS = 90_000; // 90-second timeout (Opus 4.6 + document analysis needs more time)

// --- State Types ---

export interface ChatSessionState {
  messages: ChatMessageData[];
  isStreaming: boolean;
  pendingTrade: PendingTrade | null;
  error: string | null;
}

export interface UseChatSessionReturn extends ChatSessionState {
  sendMessage: (content: string, attachments?: ChatAttachment[], accountId?: string) => Promise<void>;
  confirmTrade: () => Promise<void>;
  cancelTrade: () => void;
  newConversation: () => void;
  retryLastMessage: () => Promise<void>;
  selectedAgent: AgentType;
  setSelectedAgent: (agent: AgentType) => void;
}

// --- Reducer Actions ---

export type ChatAction =
  | { type: 'ADD_MESSAGE'; message: ChatMessageData }
  | { type: 'UPDATE_STREAMING_MESSAGE'; messageId: string; content: string }
  | { type: 'COMPLETE_STREAMING'; messageId: string }
  | { type: 'SET_STREAMING'; isStreaming: boolean }
  | { type: 'SET_PENDING_TRADE'; trade: PendingTrade | null }
  | { type: 'RESOLVE_TRADE'; trade: PendingTrade }
  | { type: 'UPDATE_LAST_TRADE'; updates: Partial<PendingTrade> }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'CLEAR_CONVERSATION' }
  | { type: 'EXPIRE_TRADE' }
  | { type: 'ADD_ACTIVITY'; messageId: string; activity: Activity }
  | { type: 'UPDATE_ACTIVITY'; messageId: string; activity: Activity };

export const initialState: ChatSessionState = {
  messages: [],
  isStreaming: false,
  pendingTrade: null,
  error: null,
};

/**
 * Pure state reducer for the chat session. Exported (named) for direct
 * property/unit testing of the Activity_Trace correlation semantics; the
 * runtime behavior is unchanged.
 */
export function chatReducer(state: ChatSessionState, action: ChatAction): ChatSessionState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.message], error: null };
    case 'UPDATE_STREAMING_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.messageId
            ? { ...msg, content: msg.content + action.content }
            : msg
        ),
      };
    case 'COMPLETE_STREAMING':
      return {
        ...state,
        isStreaming: false,
        messages: state.messages.map((msg) =>
          msg.id === action.messageId ? { ...msg, isStreaming: false } : msg
        ),
      };
    case 'SET_STREAMING':
      return { ...state, isStreaming: action.isStreaming };
    case 'SET_PENDING_TRADE':
      return { ...state, pendingTrade: action.trade };
    case 'RESOLVE_TRADE': {
      // Move the resolved trade into the message list and clear pendingTrade
      const tradeMessage: ChatMessageData = {
        id: `trade_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        tradeConfirmation: action.trade,
      };
      return {
        ...state,
        messages: [...state.messages, tradeMessage],
        pendingTrade: null,
      };
    }
    case 'SET_ERROR':
      return { ...state, error: action.error, isStreaming: false };
    case 'UPDATE_LAST_TRADE': {
      // Update the most recent trade confirmation message with new data (e.g., washSale flag)
      const msgs = [...state.messages];
      for (let idx = msgs.length - 1; idx >= 0; idx--) {
        if (msgs[idx]!.tradeConfirmation) {
          msgs[idx] = {
            ...msgs[idx]!,
            tradeConfirmation: { ...msgs[idx]!.tradeConfirmation!, ...action.updates },
          };
          break;
        }
      }
      return { ...state, messages: msgs };
    }
    case 'ADD_ACTIVITY':
      // Append a `started` activity to the target message's Activity_Trace in
      // receipt order (Requirement 6.1). Only the currently-streaming message
      // is targeted, so completed messages are never mutated (Requirement 6.4).
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.messageId
            ? { ...msg, activities: [...(msg.activities ?? []), action.activity] }
            : msg
        ),
      };
    case 'UPDATE_ACTIVITY':
      // Apply a terminal (`completed`/`failed`) activity to the target message.
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== action.messageId) return msg;
          const existing = msg.activities ?? [];
          const idx = existing.findIndex((a) => a.id === action.activity.id);
          if (idx === -1) {
            // No matching `started`: append the terminal activity rather than
            // drop it (Requirement 6.3).
            return { ...msg, activities: [...existing, action.activity] };
          }
          // Match found: update status and merge detail in place, preserving
          // list position and avoiding a duplicate (Requirement 6.2).
          const activities = existing.map((a, i) =>
            i === idx
              ? {
                  ...a,
                  status: action.activity.status,
                  detail: action.activity.detail
                    ? { ...a.detail, ...action.activity.detail }
                    : a.detail,
                }
              : a
          );
          return { ...msg, activities };
        }),
      };
    case 'CLEAR_CONVERSATION':
      return { ...state, messages: [], pendingTrade: null, error: null };
    case 'EXPIRE_TRADE': {
      if (!state.pendingTrade) return state;
      const expiredTrade = { ...state.pendingTrade, status: 'expired' as const };
      const expiredMessage: ChatMessageData = {
        id: `trade_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        tradeConfirmation: expiredTrade,
      };
      return {
        ...state,
        messages: [...state.messages, expiredMessage],
        pendingTrade: null,
      };
    }
    default:
      return state;
  }
}

// --- Helpers ---

function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function isTransientError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return [502, 503, 504].includes(error.status);
  }
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg.includes('network') || msg.includes('connection') || msg.includes('dns');
  }
  return false;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// --- Hook (internal — consumed by ChatSessionProvider) ---

export function useChatSessionInternal(): UseChatSessionReturn {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { getToken, signIn } = useAuth();

  const [sessionId, setSessionId] = useState<string>(() => uuidv4());
  const [selectedAgent, setSelectedAgent] = useState<AgentType>('analyst');

  const abortControllerRef = useRef<AbortController | null>(null);
  const tradeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUserMessageRef = useRef<string>('');

  // --- Trade Confirmation Timeout ---

  useEffect(() => {
    if (state.pendingTrade && (!state.pendingTrade.status || state.pendingTrade.status === 'pending')) {
      const expiresAt = new Date(state.pendingTrade.expiresAt).getTime();
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry <= 0) {
        dispatch({ type: 'EXPIRE_TRADE' });
        return;
      }

      tradeTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'EXPIRE_TRADE' });
      }, timeUntilExpiry);

      return () => {
        if (tradeTimeoutRef.current) {
          clearTimeout(tradeTimeoutRef.current);
          tradeTimeoutRef.current = null;
        }
      };
    }
  }, [state.pendingTrade]);

  // --- Cleanup on unmount ---

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (tradeTimeoutRef.current) {
        clearTimeout(tradeTimeoutRef.current);
      }
    };
  }, []);

  // --- Actions ---

  const sendMessage = useCallback(
    async (content: string, attachments?: ChatAttachment[], accountId?: string) => {
      const trimmed = content.trim();
      if ((!trimmed && (!attachments || attachments.length === 0)) || state.isStreaming) return;

      lastUserMessageRef.current = trimmed;

      // Add user message
      const userMessage: ChatMessageData = {
        id: generateMessageId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
        attachments,
      };
      dispatch({ type: 'ADD_MESSAGE', message: userMessage });

      // Prepare assistant message placeholder
      const assistantMessageId = generateMessageId();
      const assistantMessage: ChatMessageData = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
      };
      dispatch({ type: 'ADD_MESSAGE', message: assistantMessage });
      dispatch({ type: 'SET_STREAMING', isStreaming: true });
      dispatch({ type: 'SET_ERROR', error: null });

      // Prepare attachment payload for the API
      const attachmentPayload = attachments?.map((a) => ({
        name: a.name,
        mimeType: a.mimeType,
        base64: a.base64,
      }));

      // Retry logic with exponential backoff
      let attempt = 0;
      let lastError: unknown = null;

      while (attempt < MAX_RETRY_ATTEMPTS) {
        try {
          const token = await getToken();
          if (!token) {
            dispatch({ type: 'SET_ERROR', error: 'Authentication required. Please sign in again.' });
            return;
          }

          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          abortControllerRef.current = new AbortController();

          const timeoutId = setTimeout(() => {
            abortControllerRef.current?.abort();
          }, RESPONSE_TIMEOUT_MS);

          await sendChatMessage(
            { message: trimmed, session_id: sessionId, agent: selectedAgent, account_id: accountId, attachments: attachmentPayload },
            token,
            (chunk) => {
              clearTimeout(timeoutId);
              dispatch({
                type: 'UPDATE_STREAMING_MESSAGE',
                messageId: assistantMessageId,
                content: chunk,
              });
            },
            (toolCall) => {
              clearTimeout(timeoutId);
              if (toolCall.name === 'trade_confirmation') {
                const trade = toolCall.result as unknown as PendingTrade;
                const tradeWithExpiry: PendingTrade = {
                  ...trade,
                  expiresAt: trade.expiresAt || new Date(Date.now() + TRADE_CONFIRMATION_TIMEOUT_MS).toISOString(),
                };
                dispatch({ type: 'SET_PENDING_TRADE', trade: tradeWithExpiry });
              }
            },
            () => {
              clearTimeout(timeoutId);
              dispatch({ type: 'COMPLETE_STREAMING', messageId: assistantMessageId });
            },
            (error) => {
              clearTimeout(timeoutId);
              dispatch({ type: 'SET_ERROR', error: error.message });
              dispatch({ type: 'COMPLETE_STREAMING', messageId: assistantMessageId });
            },
            abortControllerRef.current.signal,
            (warning) => {
              console.warn('[AgentCore Memory]', warning);
            },
            (activity) => {
              clearTimeout(timeoutId);
              // Attach the activity to the streaming assistant message. A
              // `started` event appends; a terminal event updates in place (or
              // appends when unmatched). The full payload is forwarded so the
              // reducer can synthesize the unmatched-terminal append.
              if (activity.status === 'started') {
                dispatch({ type: 'ADD_ACTIVITY', messageId: assistantMessageId, activity });
              } else {
                dispatch({ type: 'UPDATE_ACTIVITY', messageId: assistantMessageId, activity });
              }
            }
          );

          return;
        } catch (error) {
          lastError = error;

          if (error instanceof AuthError) {
            dispatch({ type: 'SET_ERROR', error: 'Session expired. Please sign in again.' });
            dispatch({ type: 'COMPLETE_STREAMING', messageId: assistantMessageId });
            signIn();
            return;
          }

          if (error instanceof DOMException && error.name === 'AbortError') {
            dispatch({ type: 'SET_ERROR', error: 'Request timed out. Please try again.' });
            dispatch({ type: 'COMPLETE_STREAMING', messageId: assistantMessageId });
            return;
          }

          if (isTransientError(error) && attempt < MAX_RETRY_ATTEMPTS - 1) {
            const backoffDelay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
            await delay(backoffDelay);
            attempt++;
            continue;
          }

          break;
        }
      }

      const errorMessage =
        lastError instanceof Error
          ? lastError.message
          : 'An unexpected error occurred. Please try again.';
      dispatch({ type: 'SET_ERROR', error: errorMessage });
      dispatch({ type: 'COMPLETE_STREAMING', messageId: assistantMessageId });
    },
    [state.isStreaming, state.messages, getToken, signIn, sessionId, selectedAgent]
  );

  const confirmTrade = useCallback(async () => {
    if (!state.pendingTrade) return;
    // Guard against double-clicks
    if (state.pendingTrade.status === 'confirmed') return;

    if (tradeTimeoutRef.current) {
      clearTimeout(tradeTimeoutRef.current);
      tradeTimeoutRef.current = null;
    }

    const trade = state.pendingTrade;
    const confirmedTrade = { ...trade, status: 'confirmed' as const };

    // Move the trade to messages as confirmed (frees the pending slot for new trades)
    dispatch({ type: 'RESOLVE_TRADE', trade: confirmedTrade });

    // Block new messages while executing (prevents race conditions with DynamoDB)
    dispatch({ type: 'SET_STREAMING', isStreaming: true });

    // Execute the trade silently — listen for wash sale detection in the response
    try {
      const token = await getToken();
      if (!token) {
        dispatch({ type: 'SET_ERROR', error: 'Authentication required. Please sign in again.' });
        dispatch({ type: 'SET_STREAMING', isStreaming: false });
        return;
      }

      let washSaleDetected = false;
      const confirmMessage = `Confirmed: ${trade.action} ${trade.quantity} ${trade.symbol} at $${trade.price.toFixed(2)}`;
      await sendChatMessage(
        { message: confirmMessage, session_id: sessionId, agent: selectedAgent, account_id: trade.accountId },
        token,
        () => {},
        (toolCall) => {
          if (toolCall.name === 'trade_result' && toolCall.result.wash_sale) {
            washSaleDetected = true;
          }
        },
        () => {
          if (washSaleDetected) {
            dispatch({ type: 'UPDATE_LAST_TRADE', updates: { washSale: true } });
          }
          dispatch({ type: 'SET_STREAMING', isStreaming: false });
        },
        (error) => {
          console.error('[confirmTrade] Backend error:', error.message);
          dispatch({ type: 'SET_ERROR', error: `Trade execution failed: ${error.message}` });
          dispatch({ type: 'SET_STREAMING', isStreaming: false });
        },
        undefined,
        () => {},
      );
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Trade execution failed';
      console.error('[confirmTrade] Exception:', msg);
      dispatch({ type: 'SET_ERROR', error: msg });
      dispatch({ type: 'SET_STREAMING', isStreaming: false });
    }
  }, [state.pendingTrade, getToken, sessionId, selectedAgent]);

  const cancelTrade = useCallback(() => {
    if (!state.pendingTrade) return;

    if (tradeTimeoutRef.current) {
      clearTimeout(tradeTimeoutRef.current);
      tradeTimeoutRef.current = null;
    }

    // Move the trade to messages as cancelled (frees the pending slot)
    dispatch({ type: 'RESOLVE_TRADE', trade: { ...state.pendingTrade, status: 'cancelled' } });
  }, [state.pendingTrade]);

  const newConversation = useCallback(() => {
    if (state.pendingTrade) {
      if (tradeTimeoutRef.current) {
        clearTimeout(tradeTimeoutRef.current);
        tradeTimeoutRef.current = null;
      }
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setSessionId(uuidv4());
    setSelectedAgent('analyst');
    dispatch({ type: 'CLEAR_CONVERSATION' });
  }, [state.pendingTrade]);

  const retryLastMessage = useCallback(async () => {
    if (!lastUserMessageRef.current) return;
    await sendMessage(lastUserMessageRef.current);
  }, [sendMessage]);

  return {
    ...state,
    sendMessage,
    confirmTrade,
    cancelTrade,
    newConversation,
    retryLastMessage,
    selectedAgent,
    setSelectedAgent,
  };
}
